import { useReactiveVar } from '@apollo/client';
import { Headline1, ListItem, Meta, theme } from 'aimo-ui';
import { MenuItemWrapped } from 'components/account/menuItems/MenuComponents';
import InfoBox from 'components/common/InfoBox';
import Icon from 'components/icons/Icon';
import SvgImage from 'components/svgImage/SvgImage';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useGetCustomerRfidTokens from 'hooks/useGetCustomerRfidTokens';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useRfidStore } from 'stores/rfidStore';
import { featureCountryCode } from 'states/persistInStorage';
import styled from 'styled-components/native';
import { Pill } from 'styles/CommonLayoutStyles';
import {
  ColumnWithMargins,
  ScreenContainer,
  SheetDetailsWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { ElementWrapper } from 'styles/ListStyle';
import { CountryCode } from 'types/generatedSchemaTypes';
import { isWeb } from 'utils/deviceUtils';
import { parseFeatureFlagObject, TypeDictionary } from 'utils/featureFlagUtils';
import { useUiStore } from 'stores/uiStore';

const AddTagIcon: React.FC = () => (
  <ElementWrapper>
    <Icon name="plus" size="m" />
  </ElementWrapper>
);
const TextContainer = styled.Text`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme: appTheme }) => appTheme.colors.black};
  flex-shrink: 1;
`;

export const TagsAndMifareCard: React.FC = () => {
  const { t } = useTranslation();
  const {
    flags: { show_rfid_tag_registration_by_country },
  } = useContext(FeatureFlagsContext);
  const appCountry = useReactiveVar(featureCountryCode) as CountryCode;
  const countryObject = parseFeatureFlagObject<
    TypeDictionary<CountryCode, boolean>
  >(
    show_rfid_tag_registration_by_country,
    'show_rfid_tag_registration_by_country'
  );
  const showRfidTagRegistration = Object.keys(countryObject)
    .filter((key) => countryObject[key as CountryCode])
    .map((key) => key as CountryCode);

  const { customer } = useContext(AccountContext);
  const megaflex = customer.megaflex?.tokens;
  const megaflexTokens = megaflex?.map((token) => ({
    name: token?.name,
    active: token?.active,
  }));

  const rfidTokenList = useGetCustomerRfidTokens();

  useEffect(() => {
    useRfidStore.getState().setCurrentTagName('');
    useRfidStore.getState().setCurrentSerialNumber('');
  }, []);

  const hasNoTokens =
    (!rfidTokenList || rfidTokenList.length === 0) &&
    (!megaflexTokens || megaflexTokens.length === 0);

  const renderEditOrDeleteTag = (tagName: string, serialNumber: string) => {
    useRfidStore.getState().setCurrentTagName(tagName);
    useRfidStore.getState().setCurrentSerialNumber(serialNumber);
    useUiStore.getState().setSheetModal('editOrDeleteRfid');
  };

  return (
    <ScreenContainer mt={isWeb ? 190 : 0}>
      <ScrollView>
        <SheetDetailsWrapper pt={isWeb ? 60 : 30} pb={20}>
          {rfidTokenList?.map((token, index) => (
            <StyledWrapper key={token.name || index}>
              <MenuItemWrapped
                menuLabel={token.name || `Rfid ${index + 1}`}
                testID={`rfid-token-${index}`}
                onPress={() =>
                  renderEditOrDeleteTag(token.name, token.serialNumber)
                }
                withArrow={true}
                bold={false}
              />
            </StyledWrapper>
          ))}
          {megaflexTokens?.map((token, index) => (
            <StyledWrapper key={token.name || index}>
              <MenuItemWrapped
                menuLabel={token.name || `Mifare ${index + 1}`}
                testID={`mifare-token-${index}`}
                withArrow={false}
                rightComponent={
                  <ColumnWithMargins mr={5}>
                    <Pill
                      py={5}
                      bg={
                        token.active
                          ? theme.colors.lightBg.green
                          : theme.colors.gray100
                      }>
                      <Text>
                        {token.active
                          ? t('account.Mifare.MifareCardsActive')
                          : t('account.Mifare.MifareCardsNotActive')}
                      </Text>
                    </Pill>
                  </ColumnWithMargins>
                }
                bold={false}
              />
            </StyledWrapper>
          ))}

          {hasNoTokens && !isWeb && (
            <View>
              <SvgImage
                height={180}
                width={240}
                name="Happy-Car"
                testID="no-tags-image"
              />
              <StyledWrapper mt={20}>
                <Headline1 style={styles.emptyStateText}>
                  {t('account.tagsAndCards.noTagsMessage')}
                </Headline1>
              </StyledWrapper>
            </View>
          )}

          {showRfidTagRegistration.includes(appCountry) && (
            <ListItem
              item={{}}
              indexValue={0}
              accessibilityRole="button"
              accessible={true}
              title={t('account.tagsAndCards.addTag')}
              onPress={() => {
                useUiStore.getState().setSheetModal('rfidRegister');
              }}
              testID={'testID'}
              withBackground={theme.colors.gray50}
              iconComponent={AddTagIcon}
              arrowComponent={null}
            />
          )}
          {rfidTokenList?.length > 1 && (
            <StyledWrapper mt={15}>
              <InfoBox
                padding={15}
                margin={10}
                bgColor={theme.colors.lightBg.green}
                leftComponent={<Icon name="Bulb" size="xl" />}>
                <TextContainer>
                  <Meta>{t('account.tagsAndCards.registerRfid.info')}</Meta>
                </TextContainer>
              </InfoBox>
            </StyledWrapper>
          )}
        </SheetDetailsWrapper>
      </ScrollView>
    </ScreenContainer>
  );
};

const styles = StyleSheet.create({
  emptyStateText: {
    textAlign: 'center',
  },
});

export default TagsAndMifareCard;
